import React, { Component } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import cx from 'classnames'
import createHistory from 'history/createBrowserHistory'
import moment from 'moment-timezone'
import { Dialog, RefreshIndicator } from 'material-ui'
import BookingPopover from './BookingPopover'
import Navbar from './Navbar'
import constants from './constants'
import { addGroupedApptTypes, parseEvents, selectedApptTypeArr } from './helpers'
import fetchApi from '../../../services/fetchApi'
import styles from './styles/calendar.css'
import { logException } from '../../../services/sentry'
import CalendarDays from './CalendarDays'
import CalendarHours from './CalendarHours'
import CalendarBookings from './CalendarBookings'

const history = createHistory()

class BookingsCalendar extends Component {
  constructor(props) {
    super(props)
    this.state = {
      bookings: [],
      doctors: [],
      leaveModalOpen: false,
      loading: true,
      locations: [],
      popoverOpen: false,
      popoverAnchor: null,
      popoverIsNew: false,
      searchAllLocations: false,
      selectedApptType: '',
      selectedBooking: {},
      selectedLocationId: '',
      startOfWeek: moment().startOf('week'),
    }
  }

  componentDidMount() {
    const { activeLocation } = this.props
    const locIds = activeLocation ? [activeLocation.id] : null
    return this.fetchBookings(locIds, true, false)
  }

  componentWillReceiveProps({ activeLocation: nextLoc }) {
    const { activeLocation } = this.props
    const id = activeLocation ? activeLocation.id : ''
    const nextId = nextLoc ? nextLoc.id : ''
    if (id !== nextId) {
      this.setState({
        searchAllLocations: false,
        selectedLocationId: nextId,
      })
    }
  }

  componentDidUpdate(
    { activeLocation: prevActiveLocation },
    { startOfWeek: prevStartOfWeek },
  ) {
    const { activeLocation, hasDSO } = this.props
    const { searchAllLocations, startOfWeek } = this.state
    const locIds = activeLocation ? [activeLocation.id] : null
    if (prevStartOfWeek.format() !== startOfWeek.format()) {
      return this.fetchBookings(locIds, false, searchAllLocations)
    }
    if (!hasDSO) return false
    if (
      prevActiveLocation &&
      activeLocation &&
      prevActiveLocation.id !== activeLocation.id
    ) {
      return this.fetchBookings(locIds, false, searchAllLocations)
    }
    return false
  }

  onSelectionChange = (value, name) => this.setState({ [name]: value })

  onDateChange = (typeOfChange) => {
    const { startOfWeek: currWeek } = this.state
    let startOfWeek
    switch (typeOfChange) {
      case 'next':
        startOfWeek = currWeek.clone().add(1, 'week')
        break
      case 'prev':
        startOfWeek = currWeek.clone().subtract(1, 'week')
        break
      default:
        startOfWeek = moment().startOf('week')
        break
    }
    this.updateQueryDateParam(startOfWeek)
    this.setState({ startOfWeek })
  }

  getStartOfWeek = (date) => {
    if (!date) return moment().startOf('week')
    return moment(date).startOf('week')
  }

  getDateFromQueryParam = () => {
    const {
      location: { search },
    } = document
    const urlFormatDate = new URLSearchParams(search).get('date')
    const formattedDate = urlFormatDate
      ? moment(urlFormatDate).startOf('week')
      : moment().startOf('week')
    return formattedDate
  }

  locationQueryParams = (locationIds) => {
    if (!locationIds) return ''
    return locationIds.map(locationId => `&location_ids[]=${locationId}`).join('')
  }

  getBookings = (end, locationIds, start) => {
    const searchParams = new URLSearchParams({
      all_bookings: true,
      consumer: 'admin',
      end,
      start,
    })

    const uri = `/bookings?${searchParams}${this.locationQueryParams(locationIds)}`
    return fetchApi(uri, 'GET')
  }

  fetchBookings = async (locationIds = null, init = false, searchAll = false) => {
    try {
      if (!init) this.toggleLoading()
      const startOfWeek = this.getDateFromQueryParam()
      const start = startOfWeek.format('DD-MM-YYYY')
      const end = startOfWeek.clone().endOf('week').format('DD-MM-YYYY')
      const { bookings, doctors, locations } = await this.getBookings(
        end,
        locationIds,
        start,
      )
      const stateObj = {
        bookings,
        loading: false,
        startOfWeek,
        searchAllLocations: searchAll,
      }
      if (init) {
        stateObj.doctors = doctors
        stateObj.locations = locations
      }
      this.setState(stateObj)
    } catch (err) {
      logException(err)
      this.toggleLoading()
    }
  }

  momentBookings = bookings =>
    bookings.map(booking => ({
      ...booking,
      end: moment(booking.end),
      start: moment(booking.start),
    }))

  updateQueryDateParam = (date) => {
    history.push({
      pathname: '/openchair',
      search: `?date=${date.format('YYYY-MM-DD')}`,
    })
  }

  handleBookingClick = (event, booking, isNew) =>
    this.setState({
      popoverOpen: true,
      popoverAnchor: event.currentTarget,
      popoverIsNew: isNew,
      selectedBooking: booking,
    })

  handleAllLocationsCheckbox = (e) => {
    e.stopPropagation()
    if (this.state.loading) return
    const { activeLocation } = this.props
    const searchAll = e.target.checked
    const locIds = (searchAll || !activeLocation) ? null : [activeLocation.id]
    this.fetchBookings(locIds, false, searchAll)
  }

  leaveModalOpen = () => {
    this.setState({ leaveModalOpen: true })
  }

  handlePopoverClose = (force) => {
    const { leaveModalOpen } = this.state
    if (!leaveModalOpen || force) {
      this.setState({
        leaveModalOpen: false,
        popoverOpen: false,
      })
    }
  }

  toggleLoading = () => {
    const { loading } = this.state
    this.setState({
      loading: !loading,
    })
  }

  filterByLocation = (bookings) => {
    const {
      searchAllLocations,
      selectedLocationId: filterLocationId,
    } = this.state
    if (searchAllLocations || !filterLocationId) return bookings
    return bookings.filter(booking => booking.location_id === filterLocationId)
  }

  filterByApptType = (bookings) => {
    const { appointmentTypes } = this.props
    const { selectedApptType } = this.state
    if (!selectedApptType) return bookings
    const apptTypesArr = selectedApptTypeArr(appointmentTypes, selectedApptType)
    return bookings.filter(booking => apptTypesArr.some(type =>
      booking.appointment_type_ids.includes(type),
    ))
  }

  filteredBookings = () => {
    const { bookings } = this.state
    if (!bookings || !bookings.length) return []
    return parseEvents(this.filterByLocation(this.filterByApptType(this.momentBookings(bookings))))
  }

  objectById = arr => arr.reduce((acc, obj) => {
    const { id, ...rest } = obj
    return { ...acc, [id]: rest }
  }, {})

  render() {
    const {
      appointmentTypes,
      hasDSO,
      hasPatientPortal,
      userLocations,
    } = this.props
    const {
      doctors,
      loading,
      locations,
      popoverOpen,
      popoverIsNew,
      searchAllLocations,
      selectedApptType,
      selectedBooking,
      selectedLocationId,
      startOfWeek,
    } = this.state
    const { numHours, startOfDay } = constants
    const blur = popoverOpen || loading ? styles.blur : ''
    const apptTypeDropdown =
      (hasPatientPortal && addGroupedApptTypes(appointmentTypes)) || []
    const calendarClasses = cx(styles.calendar, blur)
    const locsAsObj = this.objectById(locations)
    const docsAsObj = this.objectById(doctors)
    const multipleLocations = userLocations && userLocations.length > 1

    return (
      <div className={styles.openchairView}>
        <Navbar
          apptTypeDropdown={apptTypeDropdown}
          handleAllLocationsCheckbox={this.handleAllLocationsCheckbox}
          hasDSO={hasDSO}
          hasPatientPortal={hasPatientPortal}
          locations={locations}
          month={startOfWeek.format('MMMM')}
          multipleLocations={multipleLocations}
          onSelectionChange={this.onSelectionChange}
          onDateChange={this.onDateChange}
          onNextClick={this.onNextClick}
          onPrevClick={this.onPrevClick}
          onTodayClick={this.onTodayClick}
          searchAllLocations={searchAllLocations}
          selectedApptType={selectedApptType}
          selectedLocationId={selectedLocationId}
        />
        <div className={calendarClasses}>
          <CalendarHours numHours={numHours} startOfDay={startOfDay} />
          <div className={styles.calendarDays}>
            <CalendarDays
              handleBookingClick={this.handleBookingClick}
              numHours={numHours}
              startOfWeek={startOfWeek}
            />
            <CalendarBookings
              bookings={this.filteredBookings()}
              locations={locsAsObj}
              doctors={docsAsObj}
              handleBookingClick={this.handleBookingClick}
              startOfDay={startOfDay}
            />
          </div>
          <Dialog
            open={popoverOpen}
            onRequestClose={this.handlePopoverClose}
            contentStyle={{ width: '500px' }}
            zDepth={2}
          >
            <BookingPopover
              doctors={doctors}
              fetchBookings={this.fetchBookings}
              isNew={popoverIsNew}
              leaveModalOpen={this.leaveModalOpen}
              locations={this.state.locations}
              onClose={this.handlePopoverClose}
              selectedApptType={selectedApptType}
              selectedBooking={selectedBooking}
              selectedLocationId={selectedLocationId}
              showSnackbar={this.handleSnackbarShow}
            />
          </Dialog>
        </div>
        {loading && (
          <div className={styles.spinner}>
            <RefreshIndicator size={60} left={0} top={0} status="loading" />
          </div>
        )}
      </div>
    )
  }
}

BookingsCalendar.propTypes = {
  activeLocation: PropTypes.shape({
    active: PropTypes.bool.isRequired,
    additional_recipients: PropTypes.arrayOf(PropTypes.object),
    city: PropTypes.string,
    country_code: PropTypes.string,
    created_at: PropTypes.string,
    doctor_id: PropTypes.string,
    earliest_same_day_reminder_hour: PropTypes.number,
    facebook_url: PropTypes.string,
    forwarding_email: PropTypes.string,
    google_url: PropTypes.string,
    id: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    name: PropTypes.string,
    office_email: PropTypes.string,
    office_phone: PropTypes.string,
    phone_numbers: PropTypes.arrayOf(PropTypes.object),
    practice_id: PropTypes.number,
    state: PropTypes.string,
    street_address: PropTypes.string,
    timezone: PropTypes.string,
    updated_at: PropTypes.string,
    website: PropTypes.string,
    yelp_url: PropTypes.string,
    zip: PropTypes.string,
  }),
  appointmentTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
  ).isRequired,
  hasDSO: PropTypes.bool,
  hasPatientPortal: PropTypes.bool,
  userLocations: PropTypes.arrayOf(PropTypes.object),
}

const mapStateToProps = ({
  account: {
    auth: { hasDSO },
    user: { locations },
  },
}) => ({
  userLocations: locations,
  hasDSO,
})

export default connect(mapStateToProps)(BookingsCalendar)



// WEBPACK FOOTER //
// ./src/components/OnlineScheduling/BookingsCalendar/index.js